import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { AgentPublicBio } from "model/agent";
import { Upload } from "model/upload";
import { RichTextEditor } from "component/shared/rich-text-editor";
import { PhotoUploadDialogButton } from "component/shared/photo-upload-dialog-button";
import { Switch } from "component/shared/switch";
import { visibleLen, ValidationErrors } from "../validation";
import * as styles from "../styles";

interface Props {
	publicBio: AgentPublicBio;
	errors: ValidationErrors;
	onUpdateField: <K extends keyof AgentPublicBio>(
		field: K,
		value: AgentPublicBio[K],
	) => void;
	onImageUpload: (field: "heroImage" | "logoImage", uploads: Upload[]) => void;
	onImageRemove: (field: "heroImage" | "logoImage") => void;
}

export const HeaderSection: React.FC<Props> = ({
	publicBio,
	errors,
	onUpdateField,
	onImageUpload,
	onImageRemove,
}) => {
	const pageHeaderTextLength = visibleLen(publicBio.pageHeaderText);

	return (
		<>
			<Mui.Grid item xs={12}>
				<Mui.Typography style={styles.sectionTitle}>Header</Mui.Typography>
				<Switch
					checked={publicBio.showHeader || false}
					label="Display header at top of Agent Bio Page"
					onChange={(checked) => onUpdateField("showHeader", checked)}
				/>
			</Mui.Grid>

			{/* Divider separating Header from the next section (Header Image when on, Logo when off) — MAX-3868. */}
			<Mui.Grid item xs={12}>
				<Mui.Divider />
			</Mui.Grid>

			{publicBio.showHeader && (
				<>
					<Mui.Grid item xs={12}>
						<Mui.Typography style={styles.sectionTitle}>
							Header Image <span style={styles.requiredAsterisk}>*</span>
						</Mui.Typography>
						{publicBio.heroImage ? (
							<Mui.Box display="flex" alignItems="center" style={styles.uploadBoxWithImage}>
								<img
									src={publicBio.heroImage.url}
									alt="Hero"
									style={styles.heroImagePreview}
								/>
								<Mui.IconButton
									size="small"
									onClick={() => onImageRemove("heroImage")}
									style={styles.removeButton}
								>
									<Icons.X size={16} />
								</Mui.IconButton>
							</Mui.Box>
						) : (
							<PhotoUploadDialogButton
								callback={(uploads) => onImageUpload("heroImage", uploads)}
								resourceType="image"
								maxFiles={1}
								label="Upload Image"
								icon={<Icons.Upload size={16} />}
								variant="text"
								style={styles.uploadButtonTertiary}
							/>
						)}
						{errors.heroImage && (
							<Mui.Typography
								variant="caption"
								color="error"
								data-error="true"
								style={styles.errorCaption}
							>
								{errors.heroImage}
							</Mui.Typography>
						)}
					</Mui.Grid>

					<Mui.Grid item xs={12}>
						<Mui.Divider />
					</Mui.Grid>

					<Mui.Grid item xs={12}>
						<Mui.Typography style={{ ...styles.sectionTitle, marginBottom: 24 }}>
							Quick Search
						</Mui.Typography>
						<Switch
							checked={publicBio.quickSearch || false}
							label="Display property quick search on header image"
							description="When enabled, a quick search bar will appear over your header image, allowing visitors to search for listings directly from your bio page. Compatible with Smart Search."
							onChange={(checked) => onUpdateField("quickSearch", checked)}
						/>
					</Mui.Grid>

					<Mui.Grid item xs={12}>
						<Mui.Divider />
					</Mui.Grid>

					<Mui.Grid item xs={12}>
						<Mui.Typography style={styles.sectionTitle}>
							Header Text
						</Mui.Typography>
						<Mui.Typography style={styles.descriptionText}>
							Add text to display over your header image on your bio page.
						</Mui.Typography>
						<RichTextEditor
							onChange={(text) => onUpdateField("pageHeaderText", text)}
							toolbarId="pageHeaderText"
							value={publicBio.pageHeaderText || ""}
						/>
						<Mui.Box display="flex" justifyContent="flex-end" mt={0.5}>
							<Mui.Typography
								variant="caption"
								color={errors.pageHeaderText ? "error" : "textSecondary"}
								data-error={!!errors.pageHeaderText}
							>
								{errors.pageHeaderText || `${pageHeaderTextLength}/120 characters`}
							</Mui.Typography>
						</Mui.Box>
					</Mui.Grid>

					<Mui.Grid item xs={12}>
						<Mui.Divider />
					</Mui.Grid>
				</>
			)}

			<Mui.Grid item xs={12}>
				<Mui.Typography style={styles.sectionTitle}>Logo</Mui.Typography>
				{publicBio.logoImage ? (
					<Mui.Box display="flex" alignItems="center" style={styles.uploadBoxWithImage}>
						<img
							src={publicBio.logoImage.url}
							alt="Logo"
							style={styles.logoImagePreview}
						/>
						<Mui.IconButton
							size="small"
							onClick={() => onImageRemove("logoImage")}
							style={styles.removeButton}
						>
							<Icons.X size={16} />
						</Mui.IconButton>
					</Mui.Box>
				) : (
					<PhotoUploadDialogButton
						callback={(uploads) => onImageUpload("logoImage", uploads)}
						resourceType="image"
						maxFiles={1}
						label="Upload Image"
						icon={<Icons.Upload size={16} />}
						variant="text"
						style={styles.uploadButtonTertiary}
					/>
				)}
			</Mui.Grid>
		</>
	);
};
